import { publicApiConfig } from '#apps/client-web/app/config/public-api';
import { AuthenticationError } from '#packages/data-collection/types/authentication-error';

import { type SerializedAudit } from '#packages/data-collection/models/audit';
import { type AuditForm } from '#packages/data-collection/models/audit-form';

export type UploadAuditResult = {
  id: string;
};

export type FileContent = Blob | ArrayBuffer;

export type UploadApiServiceOptions = {
  baseUrl: string;
};

export class UploadApiService {
  private readonly opts: UploadApiServiceOptions;

  constructor(opts: UploadApiServiceOptions) {
    this.opts = opts;
  }

  private buildHeaders(
    accessToken: string | undefined,
    extra?: Record<string, string>,
  ): HeadersInit {
    return {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...extra,
    };
  }

  private joinUrl(base: string, ...paths: string[]): string {
    const normalizedBase = base.replace(/\/+$/, '');

    const normalizedPaths = paths
      .map((p) => p.replace(/^\/+|\/+$/g, ''))
      .filter(Boolean);

    return [normalizedBase, ...normalizedPaths].join('/');
  }

  async uploadAudit({
    audit,
    forms,
    accessToken,
  }: {
    audit: SerializedAudit;
    forms: Array<AuditForm>;
    accessToken: string | undefined;
  }): Promise<UploadAuditResult> {
    const response = await fetch(this.joinUrl(this.opts.baseUrl, 'v1/audits'), {
      method: 'PATCH',
      headers: this.buildHeaders(accessToken, {
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ audit, forms }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationError();
      }
      throw new Error(
        `Audit upload failed: ${response.status} ${response.statusText}`,
      );
    }

    const body = await response.json();
    return body.data as UploadAuditResult;
  }

  async checkFileExists({
    serverAuditId,
    fileId,
    accessToken,
  }: {
    serverAuditId: string;
    fileId: string;
    accessToken: string | undefined;
  }): Promise<boolean> {
    const response = await fetch(
      this.joinUrl(
        this.opts.baseUrl,
        `v1/files/${serverAuditId}/${encodeURIComponent(fileId)}`,
      ),
      {
        method: 'HEAD',
        headers: this.buildHeaders(accessToken),
      },
    );

    if (response.ok) return true;
    if (response.status === 404) return false;
    if (response.status === 401) {
      throw new AuthenticationError();
    }
    throw new Error(
      `Check file exists failed: ${response.status} ${response.statusText}`,
    );
  }

  async getFileUploadUrl({
    serverAuditId,
    fileId,
    accessToken,
    replace = true,
  }: {
    serverAuditId: string;
    fileId: string;
    accessToken: string | undefined;
    replace?: boolean;
  }): Promise<string> {
    const response = await fetch(
      this.joinUrl(
        this.opts.baseUrl,
        `v1/files/${serverAuditId}/${encodeURIComponent(fileId)}?replace=${replace}`,
      ),
      {
        method: 'POST',
        headers: this.buildHeaders(accessToken),
      },
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationError();
      }
      throw new Error(
        `Get file upload URL failed: ${response.status} ${response.statusText}`,
      );
    }

    const body = await response.json();
    return body.data.url as string;
  }

  async uploadFileToUrl({
    url,
    content,
    mimeType,
  }: {
    url: string;
    content: FileContent;
    mimeType?: string;
  }): Promise<void> {
    const response = await fetch(url, {
      method: 'PUT',
      headers: mimeType ? { 'Content-Type': mimeType } : {},
      body:
        content instanceof Blob
          ? content
          : new Uint8Array(content as ArrayBuffer),
    });

    if (!response.ok) {
      throw new Error(
        `File upload failed: ${response.status} ${response.statusText}`,
      );
    }
  }
}

export const uploadApiService = new UploadApiService({
  baseUrl: publicApiConfig.baseUrl,
});
